/**
 * IncomeTab - Monthly salary chart + salary windows + recurring credits
 */

import React from 'react';
import { Wallet } from 'lucide-react';
import type { IncomeTabProps, PaymentWindowEntry, ClassifiedSalaryMonth, SalaryClassification } from '../../../../types/lending-report.types';
import { formatINR } from '../../../../types/lending-report.types';
import { getMonthLabel, formatShort } from '../../../../utils/lending-report-utils';

const CLASSIFICATION_COLORS: Record<SalaryClassification, string> = {
  Normal: '#6366f1',
  Missed: '#f87171',
  Double: '#7c3aed',
};

const SalaryBarChart: React.FC<{ classifiedData?: ClassifiedSalaryMonth[]; data: { month?: string; amount?: number; averageAmount?: number }[] }> = ({ classifiedData, data }) => {
  const useClassified = classifiedData && classifiedData.length > 0;
  const effectiveData = useClassified ? classifiedData : data;

  if (effectiveData.length === 0) {
    return <div className="text-gray-400 text-sm text-center py-8">No salary history available</div>;
  }

  const amounts = useClassified
    ? classifiedData!.map(d => d.amount)
    : data.map(d => d.amount || d.averageAmount || 0);
  const maxVal = Math.max(...amounts, 1);

  // Generate Y-axis tick values (4 ticks including 0)
  const yTicks = [0, Math.round(maxVal / 3), Math.round((maxVal * 2) / 3), maxVal];

  return (
    <div style={{ display: 'flex', gap: '8px', height: '180px' }}>
      {/* Y-axis labels */}
      <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', alignItems: 'flex-end', paddingBottom: '20px', minWidth: '36px' }}>
        {[...yTicks].reverse().map((tick, i) => (
          <span key={i} style={{ fontSize: '9px', color: '#9ca3af', lineHeight: 1 }}>
            {formatShort(tick)}
          </span>
        ))}
      </div>
      {/* Bars */}
      <div style={{ flex: 1, display: 'flex', alignItems: 'flex-end', gap: '6px', height: '100%' }}>
        {useClassified
          ? classifiedData!.map((entry, i) => {
              const heightPct = (entry.amount / maxVal) * 100;
              const barColor = CLASSIFICATION_COLORS[entry.classification] || '#6366f1';
              return (
                <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', height: '100%', justifyContent: 'flex-end' }}>
                  <div
                    style={{ width: '100%', backgroundColor: barColor, borderRadius: '2px 2px 0 0', minHeight: '2px', height: `${heightPct}%` }}
                    title={`${getMonthLabel(entry.month)} (${entry.classification}): ${formatINR(entry.amount)}`}
                  />
                  <span style={{ fontSize: '9px', color: '#6b7280', textAlign: 'center', width: '100%' }}>
                    {getMonthLabel(entry.month)}
                  </span>
                </div>
              );
            })
          : data.map((entry, i) => {
              const val = entry.amount || entry.averageAmount || 0;
              const heightPct = (val / maxVal) * 100;
              return (
                <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', height: '100%', justifyContent: 'flex-end' }}>
                  <div
                    style={{ width: '100%', backgroundColor: '#6366f1', borderRadius: '2px 2px 0 0', minHeight: '2px', height: `${heightPct}%` }}
                    title={`${getMonthLabel(entry.month) || `M${i + 1}`}: ${formatINR(val)}`}
                  />
                  <span style={{ fontSize: '9px', color: '#6b7280', textAlign: 'center', width: '100%' }}>
                    {getMonthLabel(entry.month) || `${i + 1}`}
                  </span>
                </div>
              );
            })}
      </div>
    </div>
  );
};

const WindowCard: React.FC<{ entry: PaymentWindowEntry; index: number }> = ({ entry, index }) => {
  const confidence = Math.round(entry.confidence * 100);
  return (
    <div style={{ padding: '12px', borderRadius: '12px', backgroundColor: '#ffffff', border: '1px solid #e5e7eb', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '8px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '28px', height: '28px', borderRadius: '8px', backgroundColor: '#f0fdf4' }}>
            <Wallet style={{ width: 14, height: 14, color: '#16a34a' }} />
          </div>
          <span style={{ fontSize: '12px', fontWeight: 500, color: '#374151' }}>Salary #{index + 1}</span>
        </div>
        <span style={{ fontSize: '12px', fontWeight: 500, color: confidence >= 70 ? '#16a34a' : confidence >= 40 ? '#d97706' : '#dc2626' }}>
          {confidence}%
        </span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '12px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <span style={{ color: '#6b7280' }}>Amount</span>
          <span style={{ fontWeight: 600, color: '#111827' }}>{formatINR(entry.averageAmount)}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <span style={{ color: '#6b7280' }}>Window</span>
          <span style={{ color: '#111827' }}>Day {entry.startDay} - {entry.endDay}</span>
        </div>
        {entry.counterparty && (
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ color: '#6b7280' }}>From</span>
            <span style={{ color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '150px' }}>{entry.counterparty}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export const IncomeTab: React.FC<IncomeTabProps> = ({ report }) => {
  return (
    <div className="space-y-6">
      {/* Month-wise Salary Credits */}
      <div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
          <h3 className="text-sm font-semibold text-gray-700">Month-wise Salary Credits</h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {([['Normal', '#6366f1'], ['Missed', '#f87171'], ['Double', '#7c3aed']] as const).map(([label, color]) => (
              <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: color }} />
                <span style={{ fontSize: '11px', color: '#6b7280' }}>{label}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="p-4 rounded-xl bg-white border border-gray-100 shadow-sm">
          <SalaryBarChart classifiedData={report.classifiedSalaryHistory} data={report.monthlySalaryHistory} />
        </div>
      </div>

      {/* Salary Windows */}
      {report.salaryWindows.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Detected Salary Windows</h3>
          <div className="grid grid-cols-2 gap-3">
            {report.salaryWindows.map((entry, i) => (
              <WindowCard key={i} entry={entry} index={i} />
            ))}
          </div>
        </div>
      )}

      {/* Recurring Credits */}
      {report.recurringCredits.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Recurring Credits</h3>
          <div className="grid grid-cols-2 gap-3">
            {report.recurringCredits.map((entry, i) => (
              <div key={i} className="p-3 rounded-xl bg-white border border-gray-100 shadow-sm text-xs">
                <div className="flex justify-between mb-1">
                  <span className="text-gray-500">Amount</span>
                  <span className="font-semibold">{formatINR(entry.averageAmount)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Window</span>
                  <span>Day {entry.startDay} - {entry.endDay}</span>
                </div>
                {entry.counterparty && (
                  <div className="flex justify-between mt-1">
                    <span className="text-gray-500">From</span>
                    <span className="truncate max-w-[150px]">{entry.counterparty}</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
